import * as React from 'react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
import styled from 'styled-components';
import { DocumentTitle } from '@helpcenter/document-title';
import { withErrorPage } from '@helpcenter/error-page';
import {
    isEnhanceRenderExperienceForKnowledgeArticlesEnabled,
    isCrossSiteConnieConnectionEnabled,
} from '@helpcenter/feature-flags';
import { getCloudId } from '@helpcenter/meta';
import * as grid from '@helpcenter/styles/src/grid';
import type { fetchHelpCenterAction } from 'epics/model/help-center';
import type { fetchArticlesAction } from 'epics/model/kb-article';
import type { RouterContext } from 'react-resource-router';
import type {
    fetchArticle as fetchArticleAction,
    fetchCrossSiteArticle as fetchCrossSiteArticleAction,
    fetchArticleSpaceKey as fetchArticleSpaceKeyAction,
} from 'state/actions/article';
import type { fetchPortalAction } from 'state/actions/portal';
import { LoadingContainer } from 'view/common/loading-container';
import type { PaperHeaderProps } from 'view/common/paper-header';
import { RaiseRequestLink } from 'view/common/raise-request-link';
import { PaperContent, SmallPaddingPaperContent } from 'view/layout/paper';
import type { WithAnalyticsEventsProps } from '@atlaskit/analytics-next';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { AnalyticsContext, TRACK_EVENT_TYPE } from '@atlassian/analytics-web-react';
import { legacyTrackEvent } from '@atlassian/help-center-common-util/analytics';
import { FireScreenEventOnMount } from '@atlassian/help-center-common-util/analytics/fire-screen-event';
import * as qs from '@atlassian/help-center-common-util/history-qs';
import Redirect from '@atlassian/help-center-common-util/redirect';
import { ConfluenceArticleIFrame } from './confluence-article-iframe';
import { FeedbackCollector } from './feedback-collector';
import type { Article as ArticleType } from 'state/persisted/kb-article/types';

export interface ArticleProps {
    projectId: number | undefined;
    portalId: string | undefined;
    categoryId: string | undefined;
    articleId: string;

    // props from the kbPage model
    title: string | undefined;
    viewUrl: string | undefined;
    space: ArticleType['space'] | undefined;
    projects: ArticleType['projects'] | undefined;
    kbLinkDomainURLs: string[];
    hostname?: string;

    isLoading: boolean;
    redirectTo: string | undefined;
    PaperHeader: React.ComponentType<PaperHeaderProps>;
    fetchArticle: typeof fetchArticleAction;
    fetchCrossSiteArticle: typeof fetchCrossSiteArticleAction;
    fetchCategoryArticles: typeof fetchArticlesAction;
    fetchPortal: typeof fetchPortalAction;
    fetchHelpCenter: typeof fetchHelpCenterAction;
    fetchLinkedSpaces: (meta?: { analyticsSuccessEvent?: unknown; analyticsFailureEvent?: unknown }) => void;
    shouldFetchCategoryArticles: boolean;
    shouldFetchPortal: boolean;
    fetchArticleSpaceKey: typeof fetchArticleSpaceKeyAction;
    portalLink: string;
    showRequestCreateButton: boolean;
    isKbServerInstance: boolean;
    isAnonymousAccount: boolean;
    isEmbeddedRoute?: boolean;
    linkedSpaces?: string[];

    query: Record<string, string>;
}

interface ArticleState {
    iframeLoading: boolean;
}

export class Article extends React.Component<ArticleProps & WithAnalyticsEventsProps & RouterContext, ArticleState> {
    // Read ?origin from search location.
    origin = qs.get('origin');
    state = {
        iframeLoading: true,
    };

    componentDidMount() {
        const { portalId, fetchHelpCenter } = this.props;
        if (!portalId) {
            fetchHelpCenter();
        }
        this.componentDidMountOrUpdate();
    }

    componentDidUpdate(prevProps: ArticleProps) {
        this.componentDidMountOrUpdate(prevProps);
    }

    isUrlhasCrossSiteTenantID() {
        const { match } = this.props;
        const cloudId = match.params.cloudId;
        if (!cloudId) return false;
        return getCloudId() !== cloudId;
    }

    isCrossSiteArticle() {
        return (
            isCrossSiteConnieConnectionEnabled() ||
            (FeatureGates.checkGate('kb-cross-site-enabled') && this.isUrlhasCrossSiteTenantID())
        );
    }

    componentDidMountOrUpdate(prevProps?: ArticleProps) {
        const {
            fetchArticle,
            fetchCrossSiteArticle,
            articleId,
            isLoading,
            projectId,
            portalId,
            categoryId,
            fetchCategoryArticles,
            fetchPortal,
            fetchLinkedSpaces,

            shouldFetchPortal,
            shouldFetchCategoryArticles,
            match,
            fetchArticleSpaceKey,
            isKbServerInstance,
        } = this.props;

        if ((!prevProps || prevProps.articleId !== articleId) && !this.isCrossSiteArticle()) {
            if (FeatureGates.checkGate('jsm_fetch_space_key_from_apollo') && !isKbServerInstance) {
                fetchArticleSpaceKey(articleId, this.getAnalyticsDataForApolloAPI());
            } else {
                fetchArticle({ portalId, id: articleId }, this.getAnalyticsData(false));
            }
            this.setState({ iframeLoading: true });
        }

        if (
            (!prevProps || prevProps.articleId !== articleId || prevProps.projectId !== projectId) &&
            projectId &&
            articleId &&
            this.isCrossSiteArticle()
        ) {
            const crossSiteTenantId = match.params.cloudId;
            if (crossSiteTenantId) {
                fetchCrossSiteArticle({ articleId, crossSiteTenantId, projectId, meta: this.getAnalyticsData(true) });
            } else {
                throw new Error('crossSiteTenantId is not present');
            }
        }

        if (!isLoading) {
            if (!prevProps || prevProps.isLoading || prevProps.articleId !== articleId) {
                this.sendArticleStats('page.viewed');
            }
        }

        if (portalId && (!prevProps || !prevProps.shouldFetchPortal) && shouldFetchPortal) {
            fetchPortal({ id: Number(portalId), expand: ['kbs', 'reqTypes', 'reqGroups', 'orderMapping'] });
        }

        if (
            projectId &&
            portalId &&
            categoryId &&
            (!prevProps || !prevProps.shouldFetchCategoryArticles) &&
            shouldFetchCategoryArticles
        ) {
            fetchCategoryArticles({ projectId, categoryId, portalId: Number(portalId), categoryDataRequired: true });
        }

        if (
            FeatureGates.checkGate('fetch-all-accesible-linked-spaces-for-user') &&
            (!prevProps || prevProps.articleId !== articleId) &&
            !this.isCrossSiteArticle()
        ) {
            fetchLinkedSpaces(this.getLinkedSpacesAnalyticsData());
        }
    }

    getLinkedSpacesAnalyticsData = () => {
        const { createAnalyticsEvent } = this.props;
        return {
            analyticsSuccessEvent: createAnalyticsEvent?.({
                action: 'linked spaces fetch succeeded',
                analyticsType: TRACK_EVENT_TYPE,
            }),

            analyticsFailureEvent: createAnalyticsEvent?.({
                action: 'linked spaces fetch failed',
                analyticsType: TRACK_EVENT_TYPE,
            }),
        };
    };

    getAnalyticsData = (isCrossSiteConnieConnection: boolean) => {
        const { createAnalyticsEvent } = this.props;
        return {
            analyticsSuccessEvent: createAnalyticsEvent?.({
                action: 'metadata fetched',
                analyticsType: TRACK_EVENT_TYPE,
                actionSubject: isCrossSiteConnieConnection ? 'CrossSiteKBArticle' : 'KBArticle',
            }),

            analyticsFailureEvent: createAnalyticsEvent?.({
                action: 'metadata fetch failed',
                analyticsType: TRACK_EVENT_TYPE,
                actionSubject: isCrossSiteConnieConnection ? 'CrossSiteKBArticle' : 'KBArticle',
            }),
        };
    };

    getAnalyticsDataForApolloAPI = () => {
        const { createAnalyticsEvent } = this.props;
        return {
            analyticsSuccessEvent: createAnalyticsEvent?.({
                action: 'metadata fetched',
                analyticsType: TRACK_EVENT_TYPE,
                actionSubject: 'KBArticleApolloAPI',
            }),

            analyticsFailureEvent: createAnalyticsEvent?.({
                action: 'metadata fetch failed',
                analyticsType: TRACK_EVENT_TYPE,
                actionSubject: 'KBArticleApolloAPI',
            }),
        };
    };

    sendArticleStats = (eventName: string) => {
        const { articleId, space, portalId, projectId, projects, title: pageTitle } = this.props;

        if (pageTitle !== undefined && space && projects) {
            const portalIdNumber = Number(portalId);
            const project = projects.find((proj) => proj.portalId === portalIdNumber);
            const serviceDeskId = project ? project.serviceDeskId : undefined;

            if (serviceDeskId) {
                legacyTrackEvent(`stats.event.kb.${eventName}`, {
                    pageTitle,
                    projectId,
                    pageId: articleId,
                    spaceKey: space.key,
                    service_desk_ids: `${serviceDeskId}`,
                });
            }
        }
    };

    onClickArticleHelpful = () => this.sendArticleStats('helpful.clicked');
    onClickArticleNotHelpful = () => this.sendArticleStats('not.helpful.clicked');
    onIFrameLoaded = () => this.setState({ iframeLoading: false });

    shouldShowRaiseRequestLink = (): boolean => {
        const { portalId, showRequestCreateButton } = this.props;
        return !!portalId && showRequestCreateButton;
    };

    getProjectId = (showEnhancedExperience: boolean) => {
        const { projects, projectId } = this.props;
        if (projectId) {
            return projectId;
        }
        if (showEnhancedExperience && projects && projects.length) {
            return projects[0].projectId;
        }
        return undefined;
    };

    render() {
        const {
            articleId,
            hostname,
            portalId,
            categoryId,
            isLoading,
            PaperHeader,
            title,
            viewUrl,
            redirectTo,
            portalLink,
            space,
            isKbServerInstance,
            isAnonymousAccount,
            isEmbeddedRoute,
            kbLinkDomainURLs,
            projectId,
            projects,
            linkedSpaces,
        } = this.props;
        const { iframeLoading } = this.state;

        if (redirectTo) {
            return <Redirect to={redirectTo} push={false} />;
        }

        const analyticContextAttribute = {
            portalId,
            projectId,
            categoryId,
            articleId,
            isKbServerInstance,
            origin: this.origin,
            spaceId: space?.id,
            articleViewedInEmbedWindow: this.props.isEmbeddedRoute,
        };

        const shouldForceIframeExperience = isKbServerInstance;
        const showEnhancedExperience =
            isEnhanceRenderExperienceForKnowledgeArticlesEnabled() && !shouldForceIframeExperience;
        const subHeading = showEnhancedExperience ? '' : title;
        const getArticleContentWidth = 1100;

        const PaperContentComponent = showEnhancedExperience ? SmallPaddingPaperContent : PaperContent;

        return isEmbeddedRoute ? (
            <IFrameContainer>
                <AnalyticsContext componentName="KBArticle" attributes={analyticContextAttribute}>
                    <LoadingContainer isLoading={isLoading}>
                        <ArticleContent contentMaxWidth={getArticleContentWidth} isEmbeddedRoute>
                            <ConfluenceArticleIFrame
                                /* this non-nullish assertion is fine because it will only be rendered when isLoading is false and isLoading === false means the article data has loaded, so viewUrl is defined */
                                /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
                                src={viewUrl!}
                                onIFrameLoaded={this.onIFrameLoaded}
                                spaceKey={space?.key}
                                articleId={articleId}
                                showEnhancedExperience={showEnhancedExperience}
                                projectId={this.getProjectId(showEnhancedExperience)}
                                isAnonymousAccount={isAnonymousAccount}
                                isEmbeddedRoute={isEmbeddedRoute}
                                kbLinkDomainURLs={kbLinkDomainURLs}
                                hostname={hostname}
                                projectAri={projects?.[0]?.projectAri}
                                isCrossSiteArticle={this.isCrossSiteArticle()}
                                linkedSpaces={linkedSpaces}
                            />
                        </ArticleContent>
                    </LoadingContainer>
                </AnalyticsContext>
            </IFrameContainer>
        ) : (
            <DocumentTitle title={title} ignoreBaseTitle>
                <AnalyticsContext componentName="KBArticle" attributes={analyticContextAttribute}>
                    <PaperContentComponent takeFullHeight={showEnhancedExperience}>
                        <PaperHeader subHeading={subHeading} description="" logoUrl="" />

                        <LoadingContainer isLoading={isLoading}>
                            <>
                                <ArticleContent contentMaxWidth={getArticleContentWidth}>
                                    <ConfluenceArticleIFrame
                                        /* this non-nullish assertion is fine because it will only be rendered when isLoading is false and isLoading === false means the article data has loaded, so viewUrl is defined */
                                        /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
                                        src={viewUrl!}
                                        onIFrameLoaded={this.onIFrameLoaded}
                                        spaceKey={space?.key}
                                        articleId={articleId}
                                        showEnhancedExperience={showEnhancedExperience}
                                        projectId={this.getProjectId(showEnhancedExperience)}
                                        isAnonymousAccount={isAnonymousAccount}
                                        kbLinkDomainURLs={kbLinkDomainURLs}
                                        hostname={hostname}
                                        projectAri={projects?.[0]?.projectAri}
                                        isCrossSiteArticle={this.isCrossSiteArticle()}
                                        linkedSpaces={linkedSpaces}
                                    />
                                </ArticleContent>
                                {!iframeLoading && (
                                    <FeedbackCollectorContainer>
                                        <FeedbackCollector
                                            key={`feedback-collector-${articleId}`}
                                            onClickYes={this.onClickArticleHelpful}
                                            onClickNo={this.onClickArticleNotHelpful}
                                        />
                                    </FeedbackCollectorContainer>
                                )}
                                {!iframeLoading && this.shouldShowRaiseRequestLink() && (
                                    <RaiseRequestContainer>
                                        <RaiseRequestLink to={portalLink} />
                                    </RaiseRequestContainer>
                                )}
                            </>
                            {!iframeLoading && <FireScreenEventOnMount />}
                        </LoadingContainer>
                    </PaperContentComponent>
                </AnalyticsContext>
            </DocumentTitle>
        );
    }
}

export default withErrorPage(Article);

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const FeedbackCollectorContainer = styled.div`
    margin-top: ${token('space.400', '32px')};
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const RaiseRequestContainer = styled.div`
    margin-top: ${token('space.200', '16px')};
`;

interface ArticleContentProps {
    contentMaxWidth: number;
    isEmbeddedRoute?: boolean;
}

// eslint-disable-next-line rulesdir/no-styled-export, @atlaskit/ui-styling-standard/no-exported-styles, @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
export const ArticleContent = styled.div<ArticleContentProps>`
    height: 100%;
    ${({ contentMaxWidth, isEmbeddedRoute }) =>
        isEmbeddedRoute
            ? `
            max-width: ${contentMaxWidth}px;
        margin-top: ${token('space.100', '8px')};
        margin-left: ${token('space.200', '16px')};
    `
            : `max-width: ${contentMaxWidth}px;
    margin-top: ${grid.multiple(4.5).px};`}
`;

// eslint-disable-next-line rulesdir/no-styled-export, @atlaskit/ui-styling-standard/no-exported-styles, @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
export const IFrameContainer = styled.main`
    display: flex;
    flex-direction: column;
    height: 100%;
    border: ${grid.multiple(1 / 8).px} solid ${token('color.border.bold', colors.N30)};
    border-radius: ${grid.multiple(3 / 8).px};
    box-sizing: border-box;
    background-color: ${token('elevation.surface', colors.N0)};
`;
